import {
	type Dtos,
	formatToDollar,
	ISODate,
	type NaicsCodeId,
	pluralize,
	type SuretyAccountId,
	SuretyType,
} from "@inrev/common";
import { FinalContractSuretyBondFormType } from "@inrev/common/lib/esm/enums";
import { Option } from "@inrev/core";
import { useFetchRecentlyUsedSfaaCodes, useFetchSfaaCodes } from "@portal/api/sfaa-code/admin";
import { isSfaaContractObligee } from "@portal/components/features/sfaa-code/sfaaCodeFilters";
import {
	FormCurrencyPercent,
	FormDatePicker,
	FormDropdown,
	FormInput,
	FormMoney,
	FormNAICSCodeSelect,
	FormNumber,
	FormSfaaCodeSelect,
	FormStateDropdown,
	FormTextArea,
	FormYesNo,
} from "@portal/components/form/fields";
import { NAICSCodeSelectInput } from "@portal/components/form/inputs/NAICSCodeSelectInput";
import { OptionBadge } from "@portal/components/form/inputs/OptionBadge";
import { Text } from "@portal/components/primitives";
import { YesNoDisplay } from "@portal/components/ui/YesNoDisplay";
import { useMemo } from "react";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";
import type { z } from "zod";
import { finalBondFormTypeOptions, SUBMITTED_PROJECT_NAME_TOOLTIP } from "../../../constants";
import {
	finalBondFormTypeToString,
	formatBondAmount,
	formatEmpty,
	formatFromISODate,
	formatProjectLocation,
} from "../../../utils";
import { formatMinimalUIAmount } from "../bond/bondUtils";
import { VIEW_SECTION_ITEM_LABEL_CLASS, ViewSectionCardEditItem } from "../ViewSectionCardEditItem";

const NOOP_ON_CHANGE = () => {};

type NaicsSubsetFilter = { label: string; subset: NaicsCodeId[] };

export type BondRequestRowProps<TForm extends FieldValues> = {
	formMethods: UseFormReturn<TForm, any, any>;
	name: Path<TForm>;
	editing: boolean;
	schema?: z.ZodObject<any>;
	partialRequiresSubmit?: boolean;
	isMinimalUIEnabled: boolean;
};

export const BidAmountInput = <TForm extends FieldValues>({
	bidAmount,
	...props
}: BondRequestRowProps<TForm> & { bidAmount: number }) => (
	<ViewSectionCardEditItem
		label="Bid Amount"
		viewComponent={formatMinimalUIAmount(bidAmount, props.isMinimalUIEnabled)}
		fieldSize="sm"
		editComponent={<FormMoney control={props.formMethods.control} name={props.name} compact />}
		{...props}
	/>
);

export const LowestBidInput = <TForm extends FieldValues>({
	actualBidAmount,
	...props
}: BondRequestRowProps<TForm> & { actualBidAmount: number | null | undefined }) => (
	<ViewSectionCardEditItem
		label="Lowest Bid Amount"
		subLabel="Principal's Bid"
		viewComponent={formatEmpty(formatToDollar(actualBidAmount))}
		fieldSize="sm"
		editComponent={<FormMoney control={props.formMethods.control} name={props.name} compact />}
		{...props}
	/>
);

export const SecondLowestBidInput = <TForm extends FieldValues>({
	secondBid,
	...props
}: BondRequestRowProps<TForm> & { secondBid: number | null | undefined }) => (
	<ViewSectionCardEditItem
		label="Second Lowest Bid"
		viewComponent={formatEmpty(formatToDollar(secondBid))}
		fieldSize="sm"
		editComponent={<FormMoney control={props.formMethods.control} name={props.name} compact />}
		{...props}
	/>
);

export const ActualBidDateInput = <TForm extends FieldValues>({
	actualBidDate,
	...props
}: BondRequestRowProps<TForm> & { actualBidDate: string | null | undefined }) => (
	<ViewSectionCardEditItem
		label="Bid Date"
		viewComponent={formatEmpty(formatFromISODate((actualBidDate as ISODate) || null))}
		fieldSize="sm"
		editComponent={
			<FormDatePicker
				control={props.formMethods.control}
				name={props.name}
				className="flex w-full"
				compact
			/>
		}
		{...props}
	/>
);

export const BidDateInput = <TForm extends FieldValues>({
	bidDate,
	...props
}: BondRequestRowProps<TForm> & { bidDate: string | null | undefined }) => (
	<ViewSectionCardEditItem
		label="Bid Date"
		viewComponent={formatEmpty(formatFromISODate((bidDate as ISODate) || null))}
		fieldSize="sm"
		editComponent={
			<FormDatePicker
				control={props.formMethods.control}
				name={props.name}
				className="flex w-full"
				compact
			/>
		}
		{...props}
	/>
);

export const ContractDateInput = <TForm extends FieldValues>({
	contractDate,
	...props
}: BondRequestRowProps<TForm> & { contractDate: string | null | undefined }) => (
	<ViewSectionCardEditItem
		label="Contract Date"
		viewComponent={formatEmpty(formatFromISODate((contractDate as ISODate) || null))}
		fieldSize="sm"
		editComponent={
			<FormDatePicker
				control={props.formMethods.control}
				name={props.name}
				className="flex w-full"
				compact
			/>
		}
		{...props}
	/>
);

/**
 * `requiredForApproval` is passed explicitly because a $0 bond amount is "missing" but not empty,
 * so the indicator can't be derived from the value. Only admin surfaces it.
 */
export const BondAmountInput = <TForm extends FieldValues>({
	bondAmount,
	requiredForApproval,
	...props
}: BondRequestRowProps<TForm> & { bondAmount: number; requiredForApproval?: boolean }) => (
	<ViewSectionCardEditItem
		label="Bond Amount"
		viewComponent={formatBondAmount(bondAmount)}
		fieldSize="sm"
		editComponent={<FormMoney control={props.formMethods.control} name={props.name} compact />}
		{...props}
		requiredForApprovalOverride={requiredForApproval}
	/>
);

export const ContractAmountInput = <TForm extends FieldValues>({
	contractAmount,
	...props
}: BondRequestRowProps<TForm> & { contractAmount: number }) => (
	<ViewSectionCardEditItem
		label="Contract Amount"
		viewComponent={formatMinimalUIAmount(contractAmount, props.isMinimalUIEnabled)}
		fieldSize="sm"
		editComponent={<FormMoney control={props.formMethods.control} name={props.name} compact />}
		{...props}
	/>
);

export const FinalBondFormTypeDropdown = <TForm extends FieldValues>({
	finalBondFormType,
	placeholder,
	...props
}: BondRequestRowProps<TForm> & {
	finalBondFormType: FinalContractSuretyBondFormType | null | undefined;
	placeholder: string;
}) => (
	<ViewSectionCardEditItem
		label="Final Bond Form Type"
		viewComponent={formatEmpty(finalBondFormTypeToString(finalBondFormType))}
		fieldSize="md"
		editComponent={
			<FormDropdown
				placeholder={placeholder}
				control={props.formMethods.control}
				options={finalBondFormTypeOptions}
				name={props.name}
				className="w-full"
				buttonClassName="text-[14px]"
				deselectable
				compact
			/>
		}
		{...props}
	/>
);

export const ScopeOfWorkInput = <TForm extends FieldValues>({
	scopeOfWork,
	accountNaicsCodes,
	...props
}: BondRequestRowProps<TForm> & {
	scopeOfWork: readonly NaicsCodeId[];
	accountNaicsCodes: readonly NaicsCodeId[];
}) => {
	const scopeOfWorkValue = useMemo(() => [...scopeOfWork], [scopeOfWork]);
	const subsetFilters = useMemo<[NaicsSubsetFilter, ...NaicsSubsetFilter[]]>(
		() => [{ label: "Account Specialties", subset: [...accountNaicsCodes] }],
		[accountNaicsCodes],
	);

	return (
		<ViewSectionCardEditItem
			label="Scope of Work"
			viewComponent={
				scopeOfWork.length > 0 ? (
					<NAICSCodeSelectInput
						title="Scope of Work"
						selectText=""
						editText=""
						onChange={NOOP_ON_CHANGE}
						value={scopeOfWorkValue}
						readonly={true}
						className="flex flex-col items-end"
					/>
				) : (
					formatEmpty(null)
				)
			}
			editComponent={
				<FormNAICSCodeSelect
					title="Select Scopes of Work"
					selectText="Select scopes of work"
					editText="Edit scopes of work"
					searchPlaceholder="Search scopes of work..."
					subsetFilters={subsetFilters}
					defaultSubsetIndex={accountNaicsCodes.length > 0 ? 0 : undefined}
					allNAICSCodesLabel="All Scopes of Work"
					inline
					control={props.formMethods.control}
					name={props.name}
				/>
			}
			{...props}
		/>
	);
};

export const ProjectNameInput = <TForm extends FieldValues>({
	projectName,
	...props
}: BondRequestRowProps<TForm> & { projectName: string | null | undefined }) => (
	<ViewSectionCardEditItem
		label="Project Name"
		viewComponent={formatEmpty(projectName)}
		fieldSize="lg"
		editComponent={
			<div className="w-full min-w-0">
				<FormInput
					control={props.formMethods.control}
					name={props.name}
					compact
					containerClassName="w-full"
				/>
			</div>
		}
		{...props}
		tooltip={SUBMITTED_PROJECT_NAME_TOOLTIP}
	/>
);

export const ProjectDescriptionInput = <TForm extends FieldValues>({
	projectDescription,
	...props
}: BondRequestRowProps<TForm> & { projectDescription: string | null | undefined }) => (
	<ViewSectionCardEditItem
		label="Project Description"
		align="start"
		viewComponent={<div className="break-words">{formatEmpty(projectDescription)}</div>}
		editComponent={
			<FormTextArea
				control={props.formMethods.control}
				name={props.name}
				className="min-h-[80px] w-full"
			/>
		}
		{...props}
	/>
);

export const ProjectDescriptionBlockquote = <TForm extends FieldValues>({
	projectDescription,
	testId,
	...props
}: BondRequestRowProps<TForm> & {
	projectDescription: string | null | undefined;
	testId?: string;
}) => (
	<div
		data-testid={testId}
		className="relative flex w-full flex-col gap-[10px] px-[10px] py-[14px]"
	>
		<div className={VIEW_SECTION_ITEM_LABEL_CLASS}>Description</div>
		{props.editing ? (
			<FormTextArea
				control={props.formMethods.control}
				name={props.name}
				className="min-h-[80px] w-full"
			/>
		) : (
			<blockquote
				data-testid={testId ? `${testId}-blockquote` : undefined}
				className="border-l-2 border-bc-default pl-3 break-words"
			>
				<Text type="body" color="secondary">
					{formatEmpty(projectDescription)}
				</Text>
			</blockquote>
		)}
	</div>
);

/** Takes `name` as a `[city, state]` pair, since the one row edits two fields. */
export const ProjectLocationInput = <TForm extends FieldValues>({
	city,
	state,
	...props
}: Omit<BondRequestRowProps<TForm>, "name"> & {
	name: Path<TForm>[];
	city: string | null | undefined;
	state: string | null | undefined;
}) => (
	<ViewSectionCardEditItem
		label="Project Location"
		partialActive={!props.editing}
		viewComponent={formatProjectLocation(city, state)}
		editComponent={
			<>
				<FormInput
					control={props.formMethods.control}
					name={props.name[0]}
					placeholder="City"
					compact
					containerClassName="w-[200px]"
				/>
				<FormStateDropdown
					control={props.formMethods.control}
					name={props.name[1]}
					placeholder="State"
					compact
					className="w-[160px]"
					optionsClassName="max-h-[230px] overflow-y-auto"
					deselectable
				/>
			</>
		}
		{...props}
	/>
);

export const TimeToCompleteInput = <TForm extends FieldValues>({
	monthsToComplete,
	...props
}: BondRequestRowProps<TForm> & { monthsToComplete: number | null | undefined }) => (
	<ViewSectionCardEditItem
		label="Time to Complete"
		viewComponent={formatEmpty(pluralize(monthsToComplete, "month", "months"))}
		fieldSize="sm"
		editComponent={
			<FormNumber control={props.formMethods.control} name={props.name} compact suffix="months" />
		}
		{...props}
	/>
);

export const WarrantyPeriodInput = <TForm extends FieldValues>({
	contractWarrantyMonths,
	...props
}: BondRequestRowProps<TForm> & { contractWarrantyMonths: number | null | undefined }) => (
	<ViewSectionCardEditItem
		label="Warranty Period"
		viewComponent={formatEmpty(pluralize(contractWarrantyMonths, "month", "months"))}
		fieldSize="sm"
		editComponent={
			<FormNumber control={props.formMethods.control} name={props.name} compact suffix="months" />
		}
		{...props}
	/>
);

export const TListingRequiredInput = <TForm extends FieldValues>({
	tListingRequired,
	...props
}: BondRequestRowProps<TForm> & { tListingRequired: boolean | null | undefined }) => (
	<ViewSectionCardEditItem
		label="Treasury Listing Required"
		viewComponent={<YesNoDisplay value={tListingRequired} />}
		editComponent={
			<FormYesNo control={props.formMethods.control} name={props.name} deselectable compact />
		}
		{...props}
	/>
);

const SfaaCodeSummary = ({ sfaaCode }: { sfaaCode: Option<Dtos.SfaaCode.Get.Response> }) =>
	Option.isSome(sfaaCode) ? (
		<div
			className="flex min-w-0 items-center gap-2"
			data-testid="admin-bond-request-sfaa-code-summary"
		>
			<OptionBadge badge={sfaaCode.code} />
			<Text type="body" color="primary">
				{sfaaCode.label}
			</Text>
		</div>
	) : (
		formatEmpty(null)
	);

export const SfaaCodeInput = <TForm extends FieldValues>({
	accountId,
	sfaaCode,
	suretyType,
	...props
}: BondRequestRowProps<TForm> & {
	accountId: SuretyAccountId;
	sfaaCode: Option<Dtos.SfaaCode.Get.Response>;
	suretyType: SuretyType;
}) => {
	const { sfaaCodes, sfaaCodesError, sfaaCodesIsLoading } = useFetchSfaaCodes({}, props.editing);
	const { recentlyUsedSfaaCodes } = useFetchRecentlyUsedSfaaCodes(
		accountId,
		{ suretyType },
		props.editing,
	);
	const initialOwnerType =
		Option.isSome(sfaaCode) && isSfaaContractObligee(sfaaCode.obligeeType)
			? sfaaCode.obligeeType
			: undefined;
	const summary = <SfaaCodeSummary sfaaCode={sfaaCode} />;

	return (
		<ViewSectionCardEditItem
			label="SFAA Code"
			viewComponent={summary}
			fieldSize="lg"
			editComponent={
				<div className="flex w-full flex-col gap-1" data-testid="admin-bond-request-sfaa-code">
					<FormSfaaCodeSelect
						control={props.formMethods.control}
						name={props.name}
						sfaaCodes={sfaaCodes}
						recentlyUsed={recentlyUsedSfaaCodes}
						isLoading={sfaaCodesIsLoading}
						suretyType={suretyType}
						initialOwnerType={initialOwnerType}
						compact
					/>
					{sfaaCodesError ? (
						<Text type="caption" color="danger">
							Couldn't load SFAA codes. Try again.
						</Text>
					) : null}
				</div>
			}
			{...props}
		/>
	);
};

export const CompletionBondInput = <TForm extends FieldValues>({
	completionBond,
	...props
}: BondRequestRowProps<TForm> & { completionBond: boolean | null | undefined }) => (
	<ViewSectionCardEditItem
		label="Completion Bond"
		viewComponent={<YesNoDisplay value={completionBond} />}
		editComponent={
			<FormYesNo control={props.formMethods.control} name={props.name} deselectable compact />
		}
		{...props}
	/>
);

/** The bid-bond variant: one amount editable as either a dollar figure or a percent of the bid. */
export const BondAmountCurrencyPercentInput = <TForm extends FieldValues>({
	bondAmount,
	requiredForApproval,
	currencyPercentNames,
	...props
}: BondRequestRowProps<TForm> & {
	bondAmount: number;
	requiredForApproval?: boolean;
	currencyPercentNames: {
		referenceSourceName: Path<TForm>;
		currencyName: Path<TForm>;
		percentName: Path<TForm>;
		amountTypeName: Path<TForm>;
	};
}) => (
	<ViewSectionCardEditItem
		label="Bond Amount"
		viewComponent={formatBondAmount(bondAmount)}
		fieldSize="sm"
		editComponent={
			<FormCurrencyPercent {...currencyPercentNames} formMethods={props.formMethods} compact />
		}
		{...props}
		requiredForApprovalOverride={requiredForApproval}
	/>
);
